import React, { useState, useEffect } from 'react'
import { 
  Save, 
  X, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Building, 
  Award,
  Clock,
  Star,
  Truck,
  Users,
  Shield,
  Settings,
  UserCheck,
  Building2
} from 'lucide-react'
import { apiClient } from '../config/api'
import { useAuth } from '../contexts/AuthContext'

// Staff Form - Create and edit staff members
// Handles all staff types with dynamic field visibility

interface FleetStaff {
  id?: number
  staff_code: string
  first_name: string
  last_name: string
  full_name?: string
  staff_type: 'DRIVER' | 'LOADER' | 'SUPERVISOR' | 'COORDINATOR' | 'MAINTENANCE' | 'SECURITY' | 'EXTERNAL_PROVIDER'
  employment_status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED'
  email?: string
  phone?: string
  hire_date: Date
  department?: string
  position?: string
  supervisor_id?: number
  driver_license_class?: string
  driver_license_number?: string
  license_expiry_date?: Date
  shift_pattern: string
  max_hours_per_day?: number
  max_hours_per_week?: number
  performance_rating?: number
  hourly_rate?: number
  monthly_salary?: number
  customer_id?: number | null
}

interface StaffFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (staff: FleetStaff) => void | Promise<void>
  editingStaff?: FleetStaff | null
  supervisors: FleetStaff[]
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const StaffForm: React.FC<StaffFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingStaff,
  supervisors,
  showMessage
}) => {
  // Form state
  const [formData, setFormData] = useState<FleetStaff>({
    staff_code: '',
    first_name: '',
    last_name: '',
    staff_type: 'DRIVER',
    employment_status: 'ACTIVE',
    email: '',
    phone: '',
    hire_date: new Date(),
    department: '',
    position: '',
    supervisor_id: undefined,
    driver_license_class: '',
    driver_license_number: '',
    license_expiry_date: undefined,
    shift_pattern: 'DAY',
    max_hours_per_day: 8,
    max_hours_per_week: 40,
    performance_rating: 0, // Will be calculated from mission metrics
    hourly_rate: undefined,
    monthly_salary: undefined
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Customer assignment: forced for tenant accounts, picker for global users
  const { customerId: authCustomerId } = useAuth()
  const [customers, setCustomers] = useState<Array<{ id: number; company_name: string; customer_code: string }>>([])
  useEffect(() => {
    if (!isOpen || authCustomerId != null) return
    apiClient.get('/customers')
      .then(res => setCustomers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCustomers([]))
  }, [isOpen, authCustomerId])

  // Initialize form with editing data
  useEffect(() => {
    if (editingStaff) {
      setFormData({
        ...editingStaff,
        hire_date: editingStaff.hire_date || new Date(),
        license_expiry_date: editingStaff.license_expiry_date || undefined,
        max_hours_per_day: editingStaff.max_hours_per_day ?? 8,
        max_hours_per_week: editingStaff.max_hours_per_week ?? 40
      })
    } else {
      // Reset form for new staff
      setFormData({
        staff_code: '',
        first_name: '',
        last_name: '',
        staff_type: 'DRIVER',
        employment_status: 'ACTIVE',
        email: '',
        phone: '',
        hire_date: new Date(),
        department: '',
        position: '',
        supervisor_id: undefined,
        driver_license_class: '',
        license_expiry_date: undefined,
        shift_pattern: 'DAY',
        max_hours_per_day: 8,
        max_hours_per_week: 40,
        performance_rating: 5.0,
        hourly_rate: undefined,
        monthly_salary: undefined
      })
    }
    setErrors({})
  }, [editingStaff, isOpen])

  // Handle form field changes
  const handleChange = (field: keyof FleetStaff, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Required fields
    if (!formData.staff_code.trim()) newErrors.staff_code = 'Staff code is required'
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required'
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required'
    if (!formData.staff_type) newErrors.staff_type = 'Staff type is required'

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Phone validation
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    // Driver-specific validation
    if (formData.staff_type === 'DRIVER') {
      if (!formData.driver_license_number) newErrors.driver_license_number = 'License number is required for drivers'
      if (!formData.driver_license_class) newErrors.driver_license_class = 'License class is required for drivers'
      if (!formData.license_expiry_date) newErrors.license_expiry_date = 'License expiry date is required for drivers'
      if (formData.license_expiry_date && formData.license_expiry_date < new Date()) {
        newErrors.license_expiry_date = 'License expiry date cannot be in the past'
      }
    }

    // Note: Performance rating removed - will be calculated from mission metrics when task management is implemented

    // Hours validation
    if (formData.max_hours_per_day < 1 || formData.max_hours_per_day > 24) {
      newErrors.max_hours_per_day = 'Daily hours must be between 1 and 24'
    }
    if (formData.max_hours_per_week < 1 || formData.max_hours_per_week > 168) {
      newErrors.max_hours_per_week = 'Weekly hours must be between 1 and 168'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      if (showMessage) {
        showMessage('Please fix the form errors before submitting', 'error')
      }
      return
    }

    setIsSubmitting(true)

    try {
      // Generate staff code if not provided
      if (!formData.staff_code.trim()) {
        const typePrefix = formData.staff_type.substring(0, 3).toUpperCase()
        const timestamp = Date.now().toString().slice(-4)
        formData.staff_code = `${typePrefix}${timestamp}`
      }

      console.log('💾 Saving staff member:', formData)
      await onSave(formData)
      
      if (showMessage) {
        showMessage(
          editingStaff 
            ? `Staff member ${formData.first_name} ${formData.last_name} updated successfully`
            : `Staff member ${formData.first_name} ${formData.last_name} created successfully`,
          'success'
        )
      }
      
      onClose()
    } catch (error) {
      // Error message already shown by the parent's save handler
      console.error('❌ Error saving staff member:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get staff type icon
  const getStaffTypeIcon = (type: string) => {
    switch (type) {
      case 'DRIVER': return <Truck className="w-5 h-5 text-blue-600" />
      case 'SUPERVISOR': return <UserCheck className="w-5 h-5 text-purple-600" />
      case 'LOADER': return <Users className="w-5 h-5 text-green-600" />
      case 'COORDINATOR': return <Settings className="w-5 h-5 text-orange-600" />
      case 'MAINTENANCE': return <Settings className="w-5 h-5 text-red-600" />
      case 'SECURITY': return <Shield className="w-5 h-5 text-gray-600" />
      default: return <User className="w-5 h-5 text-gray-600" />
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[300] overflow-hidden">
      <div className="fixed inset-0 bg-black bg-opacity-30" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          
          {/* Mac-Style Window Title Bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-b from-slate-700 to-slate-900 border-b border-slate-600 rounded-t-lg">
            {/* Mac Traffic Light Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="group w-3 h-3 bg-gradient-to-b from-red-400 to-red-500 rounded-full hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-sm border border-red-600/20 relative overflow-hidden"
                title="Close"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full"></div>
              </button>
              <button
                className="group w-3 h-3 bg-gradient-to-b from-yellow-400 to-yellow-500 rounded-full hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 shadow-sm border border-yellow-600/20 relative overflow-hidden"
                title="Minimize"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full"></div>
              </button>
              <button
                className="group w-3 h-3 bg-gradient-to-b from-green-400 to-green-500 rounded-full hover:from-green-500 hover:to-green-600 transition-all duration-200 shadow-sm border border-green-600/20 relative overflow-hidden"
                title="Maximize"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full"></div>
              </button>
            </div>

            {/* Window Title */}
            <div className="flex items-center space-x-3 text-white">
              {getStaffTypeIcon(formData.staff_type)}
              <h2 className="text-lg font-semibold">
                {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
              </h2>
            </div>

            {/* Window Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="px-3 py-1 text-xs text-slate-300 hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <span>Basic Information</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Staff Code *
                    </label>
                    <input
                      type="text"
                      value={formData.staff_code}
                      onChange={(e) => handleChange('staff_code', e.target.value.toUpperCase())}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.staff_code ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="e.g., DRV001"
                    />
                    {errors.staff_code && <p className="text-red-600 text-sm mt-1">{errors.staff_code}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Staff Type *
                    </label>
                    <select
                      value={formData.staff_type}
                      onChange={(e) => handleChange('staff_type', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.staff_type ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="DRIVER">🚛 Driver</option>
                      <option value="SUPERVISOR">👮 Supervisor</option>
                      <option value="LOADER">📦 Loader</option>
                      <option value="COORDINATOR">📋 Coordinator</option>
                      <option value="MAINTENANCE">🔧 Maintenance</option>
                      <option value="SECURITY">🛡️ Security</option>
                      <option value="EXTERNAL_PROVIDER">🏢 External Provider</option>
                    </select>
                    {errors.staff_type && <p className="text-red-600 text-sm mt-1">{errors.staff_type}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.first_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="First name"
                    />
                    {errors.first_name && <p className="text-red-600 text-sm mt-1">{errors.first_name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.last_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Last name"
                    />
                    {errors.last_name && <p className="text-red-600 text-sm mt-1">{errors.last_name}</p>}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  <span>Contact Information</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="email@company.com"
                    />
                    {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="+1-555-0123"
                    />
                    {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Building className="w-5 h-5 text-purple-600" />
                  <span>Professional Information</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department
                    </label>
                    <select
                      value={formData.department || ''}
                      onChange={(e) => handleChange('department', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select department</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Operations">Operations</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="Logistics">Logistics</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Security">Security</option>
                      <option value="Administration">Administration</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position
                    </label>
                    <input
                      type="text"
                      value={formData.position || ''}
                      onChange={(e) => handleChange('position', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Senior Driver, Fleet Supervisor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hire Date
                    </label>
                    <input
                      type="date"
                      value={formData.hire_date.toISOString().split('T')[0]}
                      onChange={(e) => handleChange('hire_date', new Date(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supervisor
                    </label>
                    <select
                      value={formData.supervisor_id || ''}
                      onChange={(e) => handleChange('supervisor_id', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No supervisor</option>
                      {supervisors.map(supervisor => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.full_name} ({supervisor.position})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employment Status
                    </label>
                    <select
                      value={formData.employment_status}
                      onChange={(e) => handleChange('employment_status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">✅ Active</option>
                      <option value="INACTIVE">⏸️ Inactive</option>
                      <option value="ON_LEAVE">🏖️ On Leave</option>
                      <option value="TERMINATED">❌ Terminated</option>
                    </select>
                  </div>

                  {authCustomerId == null && (
                    <div>
                      <label className="flex items-center space-x-1 text-sm font-medium text-gray-700 mb-2">
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <span>Customer</span>
                      </label>
                      <select
                        value={formData.customer_id != null ? String(formData.customer_id) : ''}
                        onChange={(e) => handleChange('customer_id', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Shared (all customers)</option>
                        {customers.map(c => (
                          <option key={c.id} value={c.id}>{c.company_name} ({c.customer_code})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Driver-Specific Information */}
              {formData.staff_type === 'DRIVER' && (
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Truck className="w-5 h-5 text-blue-600" />
                    <span>Driver Information</span>
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        License Number *
                      </label>
                      <input
                        type="text"
                        value={formData.driver_license_number || ''}
                        onChange={(e) => handleChange('driver_license_number', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          errors.driver_license_number ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="ML-CDL-A-001234"
                      />
                      {errors.driver_license_number && <p className="text-red-600 text-sm mt-1">{errors.driver_license_number}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        License Class *
                      </label>
                      <select
                        value={formData.driver_license_class || ''}
                        onChange={(e) => handleChange('driver_license_class', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          errors.driver_license_class ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select license class</option>
                        <option value="A">Class A - Regular License</option>
                        <option value="B">Class B - Light Commercial</option>
                        <option value="C">Class C - Heavy Commercial</option>
                        <option value="CDL-A">CDL-A - Commercial Driver License A</option>
                        <option value="CDL-B">CDL-B - Commercial Driver License B</option>
                        <option value="CDL-C">CDL-C - Commercial Driver License C</option>
                      </select>
                      {errors.driver_license_class && <p className="text-red-600 text-sm mt-1">{errors.driver_license_class}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        License Expiry Date *
                      </label>
                      <input
                        type="date"
                        value={formData.license_expiry_date?.toISOString().split('T')[0] || ''}
                        onChange={(e) => handleChange('license_expiry_date', e.target.value ? new Date(e.target.value) : undefined)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          errors.license_expiry_date ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.license_expiry_date && <p className="text-red-600 text-sm mt-1">{errors.license_expiry_date}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Work Schedule */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span>Work Schedule</span>
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift Pattern
                    </label>
                    <select
                      value={formData.shift_pattern}
                      onChange={(e) => handleChange('shift_pattern', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="DAY">☀️ Day Shift</option>
                      <option value="NIGHT">🌙 Night Shift</option>
                      <option value="ROTATING">🔄 Rotating</option>
                      <option value="ON_CALL">📞 On Call</option>
                      <option value="FLEXIBLE">⏰ Flexible</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Hours/Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={formData.max_hours_per_day}
                      onChange={(e) => handleChange('max_hours_per_day', parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.max_hours_per_day ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.max_hours_per_day && <p className="text-red-600 text-sm mt-1">{errors.max_hours_per_day}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Hours/Week
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={formData.max_hours_per_week}
                      onChange={(e) => handleChange('max_hours_per_week', parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.max_hours_per_week ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.max_hours_per_week && <p className="text-red-600 text-sm mt-1">{errors.max_hours_per_week}</p>}
                  </div>
                </div>
              </div>

              {/* Compensation Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-600" />
                  <span>Rémunération</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tarif Horaire (FCFA)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={formData.hourly_rate || ''}
                      onChange={(e) => handleChange('hourly_rate', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="15000"
                    />
                    <p className="text-xs text-gray-500 mt-1">Tarif par heure en Francs CFA</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Salaire Mensuel (FCFA)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={formData.monthly_salary || ''}
                      onChange={(e) => handleChange('monthly_salary', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="2500000"
                    />
                    <p className="text-xs text-gray-500 mt-1">Salaire mensuel en Francs CFA</p>
                  </div>
                </div>
                
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="text-sm text-gray-600">
                * Required fields
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSubmitting ? 'Saving...' : (editingStaff ? 'Update Staff' : 'Create Staff')}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default StaffForm
